/**
 * Zoho CRM Integration Component
 * Manages Zoho CRM OAuth 2.0 connection and lead operations
 */

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { orchestratorApi } from '../api/orchestrator';
import './GoogleCalendarAuth.css'; // Reuse styles

interface ZohoConnection {
  id: number;
  tenant_id: string;
  user_id: string;
  user_email: string;
  data_center_url: string;
  is_active: boolean;
  is_verified: boolean;
  last_verified_at?: string;
  organization_id?: string;
  organization_name?: string;
  last_error?: string;
  last_test_at?: string;
  created_at: string;
  updated_at: string;
  client_id_masked?: string;
  client_secret_masked?: string;
  has_refresh_token?: boolean;
  refresh_token_masked?: string;
}

interface ZohoLead {
  id?: string;
  First_Name: string;
  Last_Name: string;
  Email?: string;
  Phone?: string;
  Company?: string;
  Lead_Status?: string;
  Description?: string;
}

export const ZohoCRM: React.FC = () => {
  // Auth0 user for future use
  useAuth0();
  // Store all connections (for future display of team connections)
  const [, setConnections] = useState<ZohoConnection[]>([]);
  const [currentConnection, setCurrentConnection] = useState<{ authenticated: boolean; connection: ZohoConnection | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'credentials' | 'leads'>('credentials');

  // OAuth flow state
  const [oauthStep, setOauthStep] = useState<'input' | 'authorizing'>('input');
  const [credentials, setCredentials] = useState({
    client_id: '',
    client_secret: '',
    data_center_url: 'com',
  });

  // Lead form state
  const [leads, setLeads] = useState<ZohoLead[]>([]);
  const [leadForm, setLeadForm] = useState<ZohoLead>({
    First_Name: '',
    Last_Name: '',
    Email: '',
    Phone: '',
    Company: '',
    Lead_Status: '',
    Description: '',
  });
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [savingLead, setSavingLead] = useState(false);

  // Check for OAuth callback in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const zohoSuccess = urlParams.get('zoho_success');
    const zohoError = urlParams.get('zoho_error');

    if (zohoSuccess === 'true') {
      showMessage('success', 'Zoho CRM connected successfully!');
      // Clean URL
      window.history.replaceState({}, '', '/admin');
      loadCurrentConnection();
    } else if (zohoError) {
      showMessage('error', `Zoho CRM connection failed: ${zohoError}`);
      // Clean URL
      window.history.replaceState({}, '', '/admin');
      setOauthStep('input');
    }
  }, []);

  useEffect(() => {
    loadConnections();
    loadCurrentConnection();
  }, []);

  const loadConnections = async () => {
    try {
      const response = await fetch('/api/admin/zoho-crm/connections', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setConnections(data.connections);
      }
    } catch (error) {
      console.error('Failed to load Zoho CRM connections:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentConnection = async () => {
    try {
      const response = await fetch('/api/admin/zoho-crm/me', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      console.log('[ZohoCRM] Connection data:', data);

      if (data.ok) {
        setCurrentConnection(data);
        if (data.authenticated) {
          console.log('[ZohoCRM] User is authenticated with Zoho CRM');
        }
        setOauthStep('input');
      }
    } catch (error) {
      console.error('Failed to load current Zoho CRM connection:', error);
    }
  };

  const initiateOAuth = async () => {
    if (!credentials.client_id || !credentials.client_secret) {
      showMessage('error', 'Please enter Client ID and Client Secret');
      return;
    }

    setOauthStep('authorizing');

    try {
      const params = new URLSearchParams({
        client_id: credentials.client_id,
        client_secret: credentials.client_secret,
        data_center_url: credentials.data_center_url,
        scopes: 'ZohoCRM.modules.ALL,ZohoCRM.settings.ALL',
      });

      const response = await fetch(`/api/admin/zoho-crm/auth?${params.toString()}`, {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok && data.authUrl) {
        // Redirect to Zoho OAuth page
        window.location.href = data.authUrl;
      } else {
        showMessage('error', data.error || 'Failed to initiate OAuth flow');
        setOauthStep('input');
      }
    } catch (error) {
      console.error('Failed to initiate Zoho OAuth:', error);
      showMessage('error', 'Failed to connect to Zoho CRM');
      setOauthStep('input');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await fetch('/api/admin/zoho-crm/test', {
        method: 'POST',
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', data.message || 'Successfully connected to Zoho CRM!');
        await loadCurrentConnection();
      } else {
        showMessage('error', data.error || 'Connection test failed');
      }
    } catch (error) {
      console.error('Failed to test Zoho CRM connection:', error);
      showMessage('error', 'Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect from Zoho CRM?')) {
      return;
    }

    try {
      const response = await fetch('/api/admin/zoho-crm/disconnect', {
        method: 'DELETE',
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', 'Disconnected from Zoho CRM');
        setCurrentConnection(null);
        setCredentials({ client_id: '', client_secret: '', data_center_url: 'com' });
      } else {
        showMessage('error', data.error || 'Failed to disconnect');
      }
    } catch (error) {
      console.error('Failed to disconnect Zoho CRM:', error);
      showMessage('error', 'Failed to disconnect');
    }
  };

  const handleLoadLeads = async () => {
    setLoadingLeads(true);
    try {
      const response = await fetch('/api/admin/zoho-crm/leads?limit=20', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setLeads(data.leads || []);
        showMessage('info', `Loaded ${data.leads?.length || 0} leads`);
      } else {
        showMessage('error', data.error || 'Failed to load leads');
      }
    } catch (error) {
      console.error('Failed to load Zoho CRM leads:', error);
      showMessage('error', 'Failed to load leads');
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleCreateLead = async () => {
    if (!leadForm.First_Name || !leadForm.Last_Name) {
      showMessage('error', 'First Name and Last Name are required');
      return;
    }

    setSavingLead(true);
    try {
      const response = await fetch('/api/admin/zoho-crm/leads', {
        method: 'POST',
        headers: {
          ...(await orchestratorApi.getAuthHeaders()),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(leadForm),
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', 'Lead created successfully!');
        setLeadForm({
          First_Name: '',
          Last_Name: '',
          Email: '',
          Phone: '',
          Company: '',
          Lead_Status: '',
          Description: '',
        });
        await handleLoadLeads();
      } else {
        showMessage('error', data.error || 'Failed to create lead');
      }
    } catch (error) {
      console.error('Failed to create Zoho CRM lead:', error);
      showMessage('error', 'Failed to create lead');
    } finally {
      setSavingLead(false);
    }
  };

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  if (loading) {
    return <div className="gca-container">Loading...</div>;
  }

  return (
    <div className="gca-container">
      <h2 className="gca-title">🔗 Zoho CRM Integration</h2>

      {message && (
        <div className={`gca-message gca-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="gca-tabs">
        <button
          className={`gca-tab ${activeTab === 'credentials' ? 'gca-tab-active' : ''}`}
          onClick={() => setActiveTab('credentials')}
        >
          Connection
        </button>
        <button
          className={`gca-tab ${activeTab === 'leads' ? 'gca-tab-active' : ''}`}
          onClick={() => activeTab === 'leads' || currentConnection?.authenticated ? setActiveTab('leads') : null}
          disabled={!currentConnection?.authenticated}
          style={{ opacity: !currentConnection?.authenticated ? 0.5 : 1 }}
        >
          Leads
        </button>
      </div>

      {activeTab === 'credentials' && (
        <div className="gca-card">
          {/* Prominent connection banner */}
          {currentConnection?.authenticated && currentConnection.connection && (
            <div style={{
              background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
              color: 'white',
              padding: '16px 20px',
              borderRadius: '12px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 4px 12px rgba(72, 187, 120, 0.3)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '32px' }}>✅</span>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
                    Connected to Zoho CRM
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.9 }}>
                    Client ID: {currentConnection.connection.client_id_masked || 'N/A'}
                    {' • '}
                    Data Center: {currentConnection.connection.data_center_url?.toUpperCase() || 'COM'}
                    {currentConnection.connection.organization_name && (
                      <span> • {currentConnection.connection.organization_name}</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={handleDisconnect}
                className="gca-button gca-button-danger"
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  color: 'white',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                Disconnect
              </button>
            </div>
          )}

          {currentConnection?.authenticated && currentConnection.connection ? (
            <>
              <h3>Connection Details</h3>
              <table className="gca-table">
                <tbody>
                  <tr>
                    <td><strong>Status:</strong></td>
                    <td>
                      <span className={`gca-badge ${currentConnection.connection.is_verified ? 'gca-badge-active' : 'gca-badge-inactive'}`}>
                        {currentConnection.connection.is_verified ? 'Verified' : 'Not Verified'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Data Center:</strong></td>
                    <td>{currentConnection.connection.data_center_url}</td>
                  </tr>
                  {currentConnection.connection.organization_name && (
                    <tr>
                      <td><strong>Organization:</strong></td>
                      <td>{currentConnection.connection.organization_name}</td>
                    </tr>
                  )}
                  {currentConnection.connection.last_error && (
                    <tr>
                      <td><strong>Last Error:</strong></td>
                      <td style={{ color: '#e53e3e' }}>{currentConnection.connection.last_error}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div style={{ marginTop: '20px' }}>
                <button
                  onClick={handleTestConnection}
                  disabled={testing}
                  className="gca-button gca-button-primary"
                >
                  {testing ? 'Testing...' : 'Test Connection'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600' }}>Connect to Zoho CRM</h3>
                <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>
                  Enter your Server-Based Application credentials to connect
                </p>
              </div>

              {oauthStep === 'input' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#2d3748', marginBottom: '6px' }}>
                        Client ID <span style={{ color: '#e53e3e' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={credentials.client_id}
                        onChange={(e) => setCredentials({ ...credentials, client_id: e.target.value })}
                        placeholder="1000.XXXXXXXX"
                        className="gca-input"
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', transition: 'border-color 0.2s' }}
                        onFocus={(e) => e.target.style.borderColor = '#4299e1'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#2d3748', marginBottom: '6px' }}>
                        Client Secret <span style={{ color: '#e53e3e' }}>*</span>
                      </label>
                      <input
                        type="password"
                        value={credentials.client_secret}
                        onChange={(e) => setCredentials({ ...credentials, client_secret: e.target.value })}
                        placeholder="Your client secret"
                        className="gca-input"
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', transition: 'border-color 0.2s' }}
                        onFocus={(e) => e.target.style.borderColor = '#4299e1'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#2d3748', marginBottom: '6px' }}>
                      Data Center
                    </label>
                    <select
                      value={credentials.data_center_url}
                      onChange={(e) => setCredentials({ ...credentials, data_center_url: e.target.value })}
                      className="gca-input"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', backgroundColor: 'white', transition: 'border-color 0.2s' }}
                      onFocus={(e) => e.target.style.borderColor = '#4299e1'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    >
                      <option value="com">🇺🇸 zoho.com (United States)</option>
                      <option value="eu">🇪🇺 zoho.eu (Europe)</option>
                      <option value="in">🇮🇳 zoho.in (India)</option>
                      <option value="au">🇦🇺 zoho.com.au (Australia)</option>
                      <option value="jp">🇯🇵 zoho.jp (Japan)</option>
                      <option value="cn">🇨🇳 zoho.com.cn (China)</option>
                    </select>
                  </div>

                  <button
                    onClick={initiateOAuth}
                    disabled={!credentials.client_id || !credentials.client_secret}
                    className="gca-button gca-button-primary"
                    style={{
                      width: '100%',
                      padding: '12px 24px',
                      fontSize: '15px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: (!credentials.client_id || !credentials.client_secret) ? 'not-allowed' : 'pointer',
                      background: (!credentials.client_id || !credentials.client_secret) ? 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      transition: 'all 0.2s',
                      opacity: (!credentials.client_id || !credentials.client_secret) ? 0.6 : 1,
                    }}
                    onMouseOver={(e) => {
                      if (!credentials.client_id || !credentials.client_secret) return;
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Connect to Zoho CRM
                  </button>

                  <div style={{ marginTop: '24px', padding: '16px', background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontSize: '18px', marginRight: '8px' }}>💡</span>
                      <strong style={{ fontSize: '14px', color: '#2d3748' }}>How to create a Server-Based Application:</strong>
                    </div>
                    <ol style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#4a5568', lineHeight: '1.6' }}>
                      <li style={{ marginBottom: '6px' }}>Go to <a href="https://api-console.zoho.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#4299e1', textDecoration: 'none', fontWeight: '500' }}>api-console.zoho.com</a></li>
                      <li style={{ marginBottom: '6px' }}>Click <strong>"Add Client"</strong> → Select <strong>"Server Based Application"</strong></li>
                      <li style={{ marginBottom: '6px' }}>Homepage URL: <code style={{ background: '#edf2f7', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>https://app.forcemx.io</code></li>
                      <li style={{ marginBottom: '6px' }}>Authorized Redirect URI: <code style={{ background: '#edf2f7', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>https://app.forcemx.io/admin</code></li>
                      <li>Copy the <strong>Client ID</strong> and <strong>Client Secret</strong></li>
                    </ol>
                  </div>
                </>
              )}

              {oauthStep === 'authorizing' && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '40px', marginBottom: '20px' }}>🔄</div>
                  <p>Redirecting to Zoho CRM...</p>
                  <p style={{ fontSize: '13px', color: '#666' }}>
                    If you're not redirected automatically, <button onClick={() => setOauthStep('input')} style={{ background: 'none', border: 'none', color: '#4299e1', cursor: 'pointer', textDecoration: 'underline' }}>click here</button>
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'leads' && (
        <div className="gca-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>Leads</h3>
            <button
              onClick={handleLoadLeads}
              disabled={loadingLeads}
              className="gca-button gca-button-secondary"
            >
              {loadingLeads ? 'Loading...' : 'Refresh Leads'}
            </button>
          </div>

          {/* Create Lead Form */}
          <div style={{ marginBottom: '30px', padding: '15px', background: '#f7fafc', borderRadius: '8px' }}>
            <h4 style={{ marginTop: 0 }}>Create New Lead</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 'bold' }}>First Name *</label>
                <input
                  type="text"
                  value={leadForm.First_Name}
                  onChange={(e) => setLeadForm({ ...leadForm, First_Name: e.target.value })}
                  className="gca-input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Last Name *</label>
                <input
                  type="text"
                  value={leadForm.Last_Name}
                  onChange={(e) => setLeadForm({ ...leadForm, Last_Name: e.target.value })}
                  className="gca-input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Email</label>
                <input
                  type="email"
                  value={leadForm.Email}
                  onChange={(e) => setLeadForm({ ...leadForm, Email: e.target.value })}
                  className="gca-input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Phone</label>
                <input
                  type="tel"
                  value={leadForm.Phone}
                  onChange={(e) => setLeadForm({ ...leadForm, Phone: e.target.value })}
                  className="gca-input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Company</label>
                <input
                  type="text"
                  value={leadForm.Company}
                  onChange={(e) => setLeadForm({ ...leadForm, Company: e.target.value })}
                  className="gca-input"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Lead Status</label>
                <select
                  value={leadForm.Lead_Status}
                  onChange={(e) => setLeadForm({ ...leadForm, Lead_Status: e.target.value })}
                  className="gca-input"
                  style={{ width: '100%' }}
                >
                  <option value="">-None-</option>
                  <option value="Attempted to Contact">Attempted to Contact</option>
                  <option value="Contacted - Low (1)">Contacted - Low (1)</option>
                  <option value="Contacted - Mid (2)">Contacted - Mid (2)</option>
                  <option value="Contacted - High (3)">Contacted - High (3)</option>
                  <option value="Appt Fixed (4)">Appt Fixed (4)</option>
                  <option value="Contact in Future">Contact in Future</option>
                  <option value="Junk Lead">Junk Lead</option>
                  <option value="Lost Lead">Lost Lead</option>
                </select>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                  ⚠️ This field is controlled by Zoho Blueprint. Changes may require blueprint transitions.
                </div>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Description</label>
                <textarea
                  value={leadForm.Description}
                  onChange={(e) => setLeadForm({ ...leadForm, Description: e.target.value })}
                  className="gca-input"
                  style={{ width: '100%', minHeight: '60px' }}
                />
              </div>
            </div>
            <button
              onClick={handleCreateLead}
              disabled={savingLead || !leadForm.First_Name || !leadForm.Last_Name}
              className="gca-button gca-button-primary"
              style={{ marginTop: '10px' }}
            >
              {savingLead ? 'Creating...' : 'Create Lead'}
            </button>
          </div>

          {/* Leads List */}
          {leads.length > 0 ? (
            <table className="gca-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Company</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id}>
                    <td>{lead.First_Name} {lead.Last_Name}</td>
                    <td>{lead.Email || '-'}</td>
                    <td>{lead.Phone || '-'}</td>
                    <td>{lead.Company || '-'}</td>
                    <td><span className="gca-badge">{lead.Lead_Status || 'New'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
              {loadingLeads ? 'Loading leads...' : 'No leads found. Click "Refresh Leads" to load leads from Zoho CRM.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
